import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbDetail } from "@/components/layout/BreadcrumbContext";
import { DetailHeader } from "@/components/layout/DetailHeader";
import { SectionCard } from "@/components/layout/SectionCard";
import { CardGrid, CardGridItem } from "@/components/layout/CardGrid";
import { FormField } from "@/components/ui/FormField";
import { TextAreaField } from "@/components/ui/TextAreaField";
import { Button } from "@/components/ui/Button";
import { Info, MapPin, FileText, Trash2, Plus, Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { useSessionState } from "@/hooks/useSessionState";
import { useQuartier, useUpdateQuartier } from "@/hooks/useQuartiers";
import { QuartierUpdateSchema, type QuartierRow } from "@/schemas/quartier";
import { getCityFromPostalCode } from "@/lib/postalCodeLookup";
import { translateQuartier, type AiLang } from "@/lib/aiBienDescription";

export function QuartierDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data: quartierData, isLoading, isError } = useQuartier(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-accent" />
      </div>
    );
  }
  if (isError || !quartierData) {
    return (
      <div className="bg-danger-light border border-danger rounded-md p-4">
        <p className="text-sm text-danger font-medium">{t("parametres.quartiers.loadError")}</p>
      </div>
    );
  }

  return <QuartierDetailForm quartier={quartierData} />;
}

type QLang = "fr" | "nl" | "en";
const Q_LANGS: QLang[] = ["fr", "nl", "en"];

function pickPrimaryLang(uiLang: string): QLang {
  const code = (uiLang || "").toLowerCase().slice(0, 2);
  if (code === "nl") return "nl";
  if (code === "en") return "en";
  return "fr";
}

function descKey(lang: QLang): "descriptionFr" | "descriptionNl" | "descriptionEn" {
  return lang === "fr" ? "descriptionFr" : lang === "nl" ? "descriptionNl" : "descriptionEn";
}
function pointsKey(lang: QLang): "pointsInteretFr" | "pointsInteretNl" | "pointsInteretEn" {
  return lang === "fr" ? "pointsInteretFr" : lang === "nl" ? "pointsInteretNl" : "pointsInteretEn";
}
function sectionLabelKey(lang: QLang): string {
  return lang === "fr" ? "parametres.quartiers.descriptionFr"
       : lang === "nl" ? "parametres.quartiers.descriptionNl"
       : "parametres.quartiers.descriptionEn";
}

function QuartierDetailForm({ quartier }: { quartier: QuartierRow }) {
  const { t, i18n } = useTranslation();
  const primaryLang = pickPrimaryLang(i18n.language);
  const orderedLangs: QLang[] = [primaryLang, ...Q_LANGS.filter((l) => l !== primaryLang)];

  // One-shot purge of sessionStorage snapshots written before real data loaded (nom vide alors que le mock a un nom)
  useState(() => {
    const storageKey = `quartier-${quartier.id}`;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!parsed.nom && quartier.nom) sessionStorage.removeItem(storageKey);
      }
    } catch {
      // ignore
    }
    return null;
  });

  const [data, setData] = useSessionState(`quartier-${quartier.id}`, {
    nom: quartier.nom ?? "",
    codesPostaux: (quartier.codesPostaux ?? []).map((cp) => ({ ...cp })),
    descriptionFr: quartier.descriptionFr ?? "",
    pointsInteretFr: quartier.pointsInteretFr ?? "",
    descriptionNl: quartier.descriptionNl ?? "",
    pointsInteretNl: quartier.pointsInteretNl ?? "",
    descriptionEn: quartier.descriptionEn ?? "",
    pointsInteretEn: quartier.pointsInteretEn ?? "",
  });

  const [saveFeedback, setSaveFeedback] = useState<"idle" | "saved" | "error">("idle");
  const { mutate: saveQuartier, isPending: isSaving } = useUpdateQuartier(quartier.id);

  const [aiBusy, setAiBusy] = useState<QLang | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const handleSave = () => {
    const patch = QuartierUpdateSchema.parse(data);
    saveQuartier(patch, {
      onSuccess: () => {
        setSaveFeedback("saved");
        setTimeout(() => setSaveFeedback("idle"), 2500);
      },
      onError: () => setSaveFeedback("error"),
    });
  };

  const set = (key: string) => (value: string) =>
    setData((prev) => ({ ...prev, [key]: value }) as typeof prev);

  const updatePostalCode = (index: number, field: string, value: string) =>
    setData((prev) => ({
      ...prev,
      codesPostaux: prev.codesPostaux.map((cp, i) => {
        if (i !== index) return cp;
        const next = { ...cp, [field]: value };
        // Auto-fill commune quand on edit le code et que la commune est vide.
        // On n'écrase pas une commune déjà saisie (l'utilisateur peut avoir
        // mis une variante locale comme "Lasne chappelle St lambert" pour 1380).
        if (field === "code" && !cp.ville) {
          const city = getCityFromPostalCode(value);
          if (city) next.ville = city;
        }
        return next;
      }),
    }));

  const removePostalCode = (index: number) =>
    setData((prev) => ({
      ...prev,
      codesPostaux: prev.codesPostaux.filter((_: unknown, i: number) => i !== index),
    }));

  const addPostalCode = () =>
    setData((prev) => ({
      ...prev,
      codesPostaux: [...prev.codesPostaux, { code: "", ville: "" }],
    }));

  const primaryHasContent = Boolean(
    (data[descKey(primaryLang)] || "").trim() || (data[pointsKey(primaryLang)] || "").trim(),
  );

  const runTranslate = async (target: QLang) => {
    const tDesc = data[descKey(target)] || "";
    const tPoints = data[pointsKey(target)] || "";
    const targetHasContent = Boolean(tDesc.trim() || tPoints.trim());
    if (targetHasContent && !window.confirm(t("biens.aiTranslateConfirm"))) return;
    setAiBusy(target);
    setAiError(null);
    try {
      const res = await translateQuartier({
        lang: target as AiLang,
        sourceLang: primaryLang as AiLang,
        sourceDescription: data[descKey(primaryLang)] || "",
        sourcePointsInteret: data[pointsKey(primaryLang)] || "",
      });
      setData((prev) => ({
        ...prev,
        [descKey(target)]: res.description,
        [pointsKey(target)]: res.pointsInteret,
      }) as typeof prev);
    } catch (err) {
      console.error("[translateQuartier]", err);
      setAiError(t("biens.aiError"));
    } finally {
      setAiBusy(null);
    }
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      {saveFeedback === "saved" && (
        <div className="flex items-center gap-1.5 text-xs text-success">
          <CheckCircle size={14} />
          <span>{t("common.saved")}</span>
        </div>
      )}
      {saveFeedback === "error" && (
        <div className="flex items-center gap-1.5 text-xs text-danger">
          <AlertTriangle size={14} />
          <span>{t("common.saveError")}</span>
        </div>
      )}
      <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
        {isSaving ? t("common.saving") : t("common.save")}
      </Button>
    </div>
  );

  // Le fil d'Ariane V2 ne rend pas `actions` pour cette page (montée via le
  // wrapper générique V1InV2, sans barre de titre) — sans le DetailHeader
  // ci-dessous, ce bouton Enregistrer n'apparaissait nulle part (signalé
  // user 2026-08-19 : "il n'y a pas de bouton dans l'admin Quartiers pour
  // sauver"). Le useBreadcrumbDetail est conservé pour le surlignage sidebar.
  useBreadcrumbDetail({
    label: data.nom,
    actions: headerActions,
  }, [saveFeedback, isSaving]);

  return (
    <div>
      <div className="flex flex-col gap-6">
        <DetailHeader
          backTo="/v2/admin/quartiers"
          backLabel={t("parametres.quartiers.backToList")}
          title={data.nom}
          actions={headerActions}
        />
        {/* NOM */}
        <SectionCard title={t("parametres.quartiers.identification")} icon={Info} columns={1}>
          <FormField label={t("common.name")} value={data.nom} onChange={set("nom")} />
        </SectionCard>

        {/* CODES POSTAUX */}
        <SectionCard title={t("parametres.quartiers.postalCodes")} icon={MapPin} columns={1}>
          <div className="flex flex-col gap-4">
            {data.codesPostaux.map((cp: Record<string, string>, i: number) => (
              <div key={i} className="flex items-end gap-4">
                <div className="w-32">
                  <FormField label={t("common.postalCode")} value={cp.code} onChange={(v) => updatePostalCode(i, "code", v)} />
                </div>
                <div className="flex-1">
                  <FormField label={t("common.city")} value={cp.ville} onChange={(v) => updatePostalCode(i, "ville", v)} />
                </div>
                <button className="flex items-center gap-1 text-xs text-text-muted hover:text-danger cursor-pointer mb-2" onClick={() => removePostalCode(i)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button className="text-xs text-accent cursor-pointer flex items-center gap-1" onClick={addPostalCode}>
              <Plus size={14} />
              {t("parametres.quartiers.addPostalCode")}
            </button>
          </div>
        </SectionCard>

        {/* DESCRIPTIONS — rotation par langue UI (primaire en premier) */}
        <CardGrid>
          {orderedLangs.map((lang) => {
            const isPrimary = lang === primaryLang;
            const busy = aiBusy === lang;
            const translateDisabled = !primaryHasContent || aiBusy !== null;
            return (
              <CardGridItem key={lang}>
                <div className="bg-surface border border-border rounded-lg shadow-sm p-6 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-semibold tracking-widest uppercase text-text-secondary flex items-center gap-2">
                      <FileText size={16} className="text-text-muted" />
                      {t(sectionLabelKey(lang))}
                    </h3>
                    {!isPrimary && (
                      <button
                        type="button"
                        className="text-xs text-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={translateDisabled}
                        onClick={() => runTranslate(lang)}
                      >
                        {busy ? t("biens.aiTranslating") : t("common.translate")}
                      </button>
                    )}
                  </div>
                  <div className="border-b border-border mb-5" />
                  <div className="flex flex-col gap-6">
                    <TextAreaField
                      label={t("parametres.quartiers.description")}
                      value={data[descKey(lang)] ?? ""}
                      onChange={set(descKey(lang))}
                    />
                    <TextAreaField
                      label={t("parametres.quartiers.pointsInteret")}
                      value={data[pointsKey(lang)] ?? ""}
                      onChange={set(pointsKey(lang))}
                    />
                  </div>
                  {aiError && isPrimary && aiBusy === null && (
                    <p className="text-xs text-red-600 mt-3">{aiError}</p>
                  )}
                </div>
              </CardGridItem>
            );
          })}
        </CardGrid>
      </div>
    </div>
  );
}
